import React, { useRef, useState } from "react";
import { Checkbox, FormControlLabel, Menu } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import {
	ChipAndExportContainer,
	FiltersContainer,
	MenuItemContentWrapper,
	MenuItemWrapper,
	ResultsAndSearchContainer,
	ResultsText,
	SearchContainer,
	StyledChip
} from "./ProcessedTrucksListFilters.styles";
import { models } from "types/api/viewModels";
import SuggestedSearch from "components/Common/SuggestedSearch/SuggestedSearch";
import ExportButtons from "components/ExportFile/ExportButtons";
import { generateProcessedTrucksExcel } from "components/Trucks/helpers/ProcessedTrucksExportHelper";
import { ExportTrucksText, SearchTrucksPlaceholderText, TrucksStatusDefaultLabel } from "constants/text";

interface ProcessedTrucksFiltersProps {
	statusFilter: string[];
	setStatusFilter: (status: string[]) => void;
	resultsCount: number;
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	searchOptions: string[];
	statuses: { value: string; label: string }[];
	filteredTrucks: models["TruckViewModel"][] | undefined;
}

const ProcessedTrucksFilters: React.FC<ProcessedTrucksFiltersProps> = ({
	statusFilter,
	setStatusFilter,
	resultsCount,
	searchTerm,
	setSearchTerm,
	searchOptions,
	statuses,
	filteredTrucks
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);

	const handleToggle = () => setIsOpen((prev) => !prev);

	const handleStatusChange = (status: string) => {
		const isSelected = statusFilter.includes(status);
		const updatedStatuses = isSelected ? statusFilter.filter((s) => s !== status) : [...statusFilter, status];
		setStatusFilter(updatedStatuses);
	};

	const handleClear = () => {
		setStatusFilter([]);
	};

	const getChipLabel = () => {
		if (statusFilter.length === 0) {
			return TrucksStatusDefaultLabel;
		} else if (statusFilter.length === 1) {
			const selectedStatus = statuses.find((status) => status.value === statusFilter[0]);
			return selectedStatus ? selectedStatus.label : TrucksStatusDefaultLabel;
		} else {
			return `${TrucksStatusDefaultLabel} (${statusFilter.length})`;
		}
	};

	return (
		<FiltersContainer>
			<ChipAndExportContainer ref={anchorRef}>
				<StyledChip
					clickable
					deleteIcon={
						statusFilter.length > 0 ? (
							<CancelIcon
								cursor="pointer"
								onClick={handleClear}
							/>
						) : undefined
					}
					onDelete={statusFilter.length > 0 ? handleClear : undefined}
					variant="outlined"
					label={getChipLabel()}
					onClick={handleToggle}
					icon={<ExpandMoreRounded />}
					color={statusFilter.length === 0 ? "primary" : "secondary"}
				/>
				<Menu
					open={isOpen}
					anchorEl={anchorRef.current}
					onClose={handleToggle}
				>
					{statuses.map((status) => (
						<MenuItemWrapper key={status.value}>
							<MenuItemContentWrapper>
								<FormControlLabel
									control={
										<Checkbox
											checked={statusFilter.includes(status.value)}
											onChange={() => handleStatusChange(status.value)}
										/>
									}
									label={status.label}
								/>
							</MenuItemContentWrapper>
						</MenuItemWrapper>
					))}
				</Menu>
				{filteredTrucks && filteredTrucks.length > 0 && (
					<ExportButtons
						data-testid="processed-trucks-export-buttons"
						buttonHeader={ExportTrucksText}
						generateExcel={() => generateProcessedTrucksExcel(filteredTrucks)}
						largePDF={false}
						isOnlyExcel
					/>
				)}
			</ChipAndExportContainer>
			<ResultsAndSearchContainer>
				<ResultsText data-testid="trucks-search-results">Showing {resultsCount} results</ResultsText>
				<SearchContainer>
					<SuggestedSearch
						ariaLabel="Search Trucks"
						dataTestId="processed-truck-search"
						dropDownOpenOnChar={1}
						placeholder={SearchTrucksPlaceholderText}
						handleSearch={(_, value) => setSearchTerm(value ?? "")}
						id="processed-truck-search"
						searchOptions={searchOptions ?? []}
						searchValue={searchTerm ?? ""}
						size="small"
						autoFocus={false}
					/>
				</SearchContainer>
			</ResultsAndSearchContainer>
		</FiltersContainer>
	);
};

export default ProcessedTrucksFilters;
